import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, probeOwner } from "../lib/api";
import type { Shader, ShaderInput } from "../types";
import { ShaderRunner, tryCompile } from "../lib/ShaderRunner";
import { BrandMark } from "../components/BrandMark";
import { GlslEditor } from "../components/GlslEditor";
import { isoToday, slugify } from "../lib/slug";

const FRAG_TEMPLATE = `precision highp float;
uniform float u_t;
uniform vec2  u_r;

void main() {
  vec2 p = (gl_FragCoord.xy - u_r * .5) / u_r.y;
  float v = .5 + .5 * sin(u_t + length(p) * 8.);
  vec3 c = mix(vec3(.04, .08, .07), vec3(.35, 1., .82), v);
  gl_FragColor = vec4(c, 1.);
}
`;

type Mode = { kind: "new" } | { kind: "edit"; id: string };

export function Admin() {
  const nav = useNavigate();
  const [shaders, setShaders] = useState<Shader[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<Mode>({ kind: "new" });
  const [email, setEmail] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    probeOwner().then((e) => {
      setEmail(e);
      setAuthChecked(true);
    });
    refresh();
  }, []);

  const refresh = () => {
    setLoading(true);
    api
      .list()
      .then((data) => {
        setShaders(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  if (authChecked && !email) {
    return (
      <div className="app">
        <div className="grain" aria-hidden />
        <header className="masthead">
          <div className="brand" style={{ color: "var(--accent)" }}>
            <BrandMark />
            <div className="brand__text">
              <h1 className="brand__name">shader brew · admin</h1>
              <span className="brand__tag">private — cloudflare access required</span>
            </div>
          </div>
        </header>
        <div className="notice">
          You aren't signed in via Cloudflare Access. Visit this URL through the Access-protected
          domain, or check your email allow-list.
        </div>
      </div>
    );
  }

  const current = mode.kind === "edit" ? shaders.find((s) => s.id === mode.id) : null;

  return (
    <div className="app">
      <div className="grain" aria-hidden />
      <header className="masthead">
        <div className="mast__top">
          <Link to="/" className="brand">
            <BrandMark />
            <div className="brand__text">
              <h1 className="brand__name">shader brew · admin</h1>
              <span className="brand__tag">{email ?? "unknown"}</span>
            </div>
          </Link>
          <div className="mast__stats">
            <div>
              <b>{shaders.length}</b>
              <span>shaders</span>
            </div>
          </div>
        </div>
      </header>

      <div className="toolbar">
        <button className="btn btn--p" onClick={() => setMode({ kind: "new" })}>
          + new shader
        </button>
        <button className="btn" onClick={refresh}>
          refresh
        </button>
        <span className="toolbar__spacer" />
        <button className="btn" onClick={() => nav("/")}>
          back to gallery
        </button>
      </div>

      <div className="admin">
        <aside className="admin__list">
          {loading && <div className="notice">loading…</div>}
          {!loading &&
            shaders.map((s) => (
              <button
                key={s.id}
                className={`admin__row ${
                  mode.kind === "edit" && mode.id === s.id ? "on" : ""
                }`}
                onClick={() => setMode({ kind: "edit", id: s.id })}
              >
                <b>w{String(s.week).padStart(2, "0")}</b>
                <span>{s.title}</span>
                <em>{s.date}</em>
              </button>
            ))}
        </aside>

        <section>
          {mode.kind === "new" && (
            <ShaderForm
              key="new"
              initial={blank(shaders)}
              submitLabel="create"
              onSubmit={async (s) => {
                const created = await api.create(s);
                refresh();
                setMode({ kind: "edit", id: created.id });
              }}
            />
          )}
          {mode.kind === "edit" && current && (
            <ShaderForm
              key={current.id}
              initial={current}
              submitLabel="save"
              onSubmit={async (s) => {
                const updated = await api.update(current.id, s);
                setShaders((all) => all.map((x) => (x.id === updated.id ? updated : x)));
              }}
              onDelete={async () => {
                if (!confirm(`delete ${current.id}?`)) return;
                await api.remove(current.id);
                setMode({ kind: "new" });
                refresh();
              }}
            />
          )}
          {mode.kind === "edit" && !current && !loading && (
            <div className="notice">selected shader no longer exists.</div>
          )}
        </section>
      </div>
    </div>
  );
}

function blank(shaders: Shader[]): ShaderInput {
  const nextWeek = (shaders[0]?.week ?? 0) + 1;
  return {
    id: "",
    week: nextWeek,
    title: "",
    date: isoToday(),
    tags: [],
    notes: [],
    frag: FRAG_TEMPLATE,
  };
}

type FormProps = {
  initial: ShaderInput;
  submitLabel: string;
  onSubmit: (s: ShaderInput) => Promise<void>;
  onDelete?: () => Promise<void>;
};

function ShaderForm({ initial, submitLabel, onSubmit, onDelete }: FormProps) {
  const [title, setTitle] = useState(initial.title);
  const [id, setId] = useState(initial.id);
  const [week, setWeek] = useState(initial.week);
  const [date, setDate] = useState(initial.date);
  const [tagsStr, setTagsStr] = useState(initial.tags.join(", "));
  const [notesStr, setNotesStr] = useState(initial.notes.join("\n\n"));
  const [frag, setFrag] = useState(initial.frag);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const idTouched = useRef(initial.id !== "");

  // Auto-derive slug from title until the id field is touched manually.
  useEffect(() => {
    if (!idTouched.current) setId(slugify(title));
  }, [title]);

  const previewRef = useRef<HTMLCanvasElement>(null);
  const runnerRef = useRef<ShaderRunner | null>(null);

  // Recompile preview only when GLSL is valid; otherwise leave the last good
  // version on screen and surface the error.
  const previewFrag = useMemo(() => {
    const e = tryCompile(frag);
    if (e) return null;
    return frag;
  }, [frag]);

  useEffect(() => {
    if (!previewRef.current || !previewFrag) return;
    const r = new ShaderRunner(previewRef.current, previewFrag);
    runnerRef.current = r;
    r.start();
    return () => {
      r.dispose();
      runnerRef.current = null;
    };
  }, [previewFrag]);

  const previewError = useMemo(() => tryCompile(frag), [frag]);

  const submit = async () => {
    setErr(null);
    if (!id) return setErr("id required");
    if (!title) return setErr("title required");
    if (previewError) return setErr(previewError);
    setBusy(true);
    try {
      await onSubmit({
        id,
        week,
        title,
        date,
        tags: tagsStr
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        notes: notesStr.split(/\n\n+/).map((p) => p.trim()).filter(Boolean),
        frag,
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="editor__preview">
        <canvas ref={previewRef} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div className="field">
          <label>title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Plasma Bloom" />
        </div>
        <div className="field">
          <label>id (slug)</label>
          <input
            value={id}
            onChange={(e) => {
              idTouched.current = true;
              setId(slugify(e.target.value));
            }}
            placeholder="plasma-bloom"
          />
        </div>
        <div className="field">
          <label>week</label>
          <input
            type="number"
            value={week}
            onChange={(e) => setWeek(parseInt(e.target.value, 10) || 0)}
          />
        </div>
        <div className="field">
          <label>date</label>
          <input value={date} onChange={(e) => setDate(e.target.value)} placeholder="2026.04.14" />
        </div>
      </div>

      <div className="field">
        <label>tags (comma-separated)</label>
        <input value={tagsStr} onChange={(e) => setTagsStr(e.target.value)} placeholder="noise, warp" />
      </div>

      <div className="field">
        <label>notes (paragraphs separated by blank lines)</label>
        <textarea value={notesStr} onChange={(e) => setNotesStr(e.target.value)} />
      </div>

      <div className="field">
        <label>shader.frag</label>
        <div className="code">
          <GlslEditor value={frag} onChange={setFrag} minHeight="320px" />
          {previewError && <pre className="code__err">{previewError}</pre>}
        </div>
      </div>

      {err && (
        <div className="notice" style={{ borderColor: "oklch(.4 .15 28)", color: "oklch(.85 .16 28)" }}>
          {err}
        </div>
      )}

      <div className="toolbar" style={{ marginTop: 18, borderBottom: "none", paddingBottom: 0 }}>
        <button className="btn btn--p" onClick={submit} disabled={busy}>
          {busy ? "…" : submitLabel}
        </button>
        {onDelete && (
          <button className="btn btn--danger" onClick={onDelete} disabled={busy}>
            delete
          </button>
        )}
      </div>
    </div>
  );
}
